import { faro, getWebInstrumentations, initializeFaro, LogLevel } from '@grafana/faro-web-sdk';
import { TracingInstrumentation } from '@grafana/faro-web-tracing';
import { isbot } from 'isbot';
import LogServiceHelper from './log.service.helper';
import { ConfigurationService } from '../configuration.service';
import { ClientSideStorageService } from '../client-side-storage.service';
import { ClientSideStorageType } from '../../models/client-side-storage-type.enum';

export default class GrafanaLogService {
  private static samplingRate: number = 0; // Use also as a flag to check if Grafana logging is enabled (samplingRate > 0)
  private static initialized: boolean = false;

  public static init(version: string) {
    const useReverseProxy = ConfigurationService.get('grafana.usereverseproxy') === 'true';
    const parsedSamplingRate = parseFloat(ConfigurationService.get('grafana.samplingrate') || '0');
    const normalizedSamplingRate = isNaN(parsedSamplingRate) ? 0 : parsedSamplingRate;
    GrafanaLogService.samplingRate = Math.max(0, Math.min(1, normalizedSamplingRate));

    if (GrafanaLogService.samplingRate === 0) return;
    if (!ClientSideStorageService.isConsentGiven(ClientSideStorageType.Analytics)) return;
    if (GrafanaLogService.initialized) return;

    initializeFaro({
      url: useReverseProxy ? '/collect/96104a2fb843691ad5811ce808b164fe' : 'https://faro-collector-prod-eu-north-0.grafana.net/collect/96104a2fb843691ad5811ce808b164fe',
      app: {
        name: 'vasttrafikse-frontend',
        version: version,
        environment: ConfigurationService.get('environment') || 'development',
      },

      sessionTracking: {
        samplingRate: isbot(navigator.userAgent) ? 0 : GrafanaLogService.samplingRate,
      },

      instrumentations: [
        // Mandatory, omits default instrumentations otherwise.
        ...getWebInstrumentations(),

        // Tracing package to get end-to-end visibility for HTTP requests.
        new TracingInstrumentation({
          instrumentationOptions: {
            propagateTraceHeaderCorsUrls: [/labs-api.*\.vasttrafik\.se/],
          },
        }),
      ],
    });

    GrafanaLogService.initialized = true;
  }

  public static isEnabled(): boolean {
    return GrafanaLogService.initialized && GrafanaLogService.samplingRate > 0;
  }

  public static logInfo(message?: string) {
    if (!GrafanaLogService.isEnabled()) return;
    if (message === undefined) return;
    faro.api.pushLog([message], {
      level: LogLevel.INFO,
    });
  }

  public static logError(error: unknown, logMessage?: string) {
    if (!GrafanaLogService.isEnabled()) return;
    faro.api.pushError(LogServiceHelper.normalizeError(error), {
      context: logMessage ? { message: logMessage } : undefined,
    });
  }
}
